import { useState, useEffect, useRef } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EmailIcon from "@mui/icons-material/Email";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import GarpacDailyReports from "./GarpacDailyReports";
import GarpacWeeklyReports from "./GarpacWeeklyReports";
import GarpacMonthlyReports from "./GarpacMonthlyReports";

// Import SVG icons (Settings)
import { ReactComponent as SettingsIconSVG } from "../Icons/Settings.svg";
import BoomiReports from "./BoomiDashboard";
import apiService from "../services/apiService";

/* COLORS */
const BLUE_BORDER = "#4AA4FF";
const BG_PANEL = "#F5FAFF";

const GarpacReportsContainer = ({ defaultTab = "monitored" }) => {
  const [selectedTab] = useState(defaultTab); // "monitored" or "failure" - fixed, no switching
  const [selectedPeriod, setSelectedPeriod] = useState("daily");
  const [selectedApplication, setSelectedApplication] = useState("");
  const [selectedEnvironment, setSelectedEnvironment] = useState("");
  const [applications, setApplications] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Refs for child components (GARPAC)
  const dailyReportRef = useRef(null);
  const weeklyReportRef = useRef(null);
  const monthlyReportRef = useRef(null);

  // Refs for Boomi component
  const boomiDailyReportRef = useRef(null);
  const boomiWeeklyReportRef = useRef(null);
  const boomiMonthlyReportRef = useRef(null);

  // Daily report controls
  const [dateMonitored, setDateMonitored] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [showEmailSubject, setShowEmailSubject] = useState(false);
  const [emailConfigOpen, setEmailConfigOpen] = useState(false);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);

  // Boomi email configuration states
  const [boomiShowEmailSubject, setBoomiShowEmailSubject] = useState(false);
  const [boomiEmailConfigOpen, setBoomiEmailConfigOpen] = useState(false);
  const [boomiEmailConfirmOpen, setBoomiEmailConfirmOpen] = useState(false);

  // Weekly report controls
  const [dateFrom, setDateFrom] = useState("2026-01-26");
  const [dateTo, setDateTo] = useState("2026-02-01");

  // Monthly report controls
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });

  // Boomi/Failure Report controls
  const [boomiDateType, setBoomiDateType] = useState("reported");
  const [boomiFromDate, setBoomiFromDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [boomiToDate, setBoomiToDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [boomiEnvId, setBoomiEnvId] = useState("");
  const [boomiAppId, setBoomiAppId] = useState("");
  const [boomiEnvironments, setBoomiEnvironments] = useState([]);
  const [boomiApplications, setBoomiApplications] = useState([]);
  
  // Flags to track if we've auto-selected defaults
  const [hasAutoSelectedEnv, setHasAutoSelectedEnv] = useState(false);
  const [hasAutoSelectedApp, setHasAutoSelectedApp] = useState(false);

  // Helper function to convert date string to Date object
  const toDateObj = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr + 'T00:00:00');
  };

  // Fetch environments for GARPAC reports first
  useEffect(() => {
    const fetchEnvironments = async () => {
      try {
        const data = await apiService.get('/environments');
        setEnvironments(data || []);
        
        // Auto-select the first environment if available
        if (data && data.length > 0) {
          setSelectedEnvironment(data[0].env_id);
        }
      } catch (error) {
        console.error("Error fetching environments:", error);
        setEnvironments([]);
      }
    };
    fetchEnvironments();
  }, []);

  // Fetch applications based on selected environment (cascading)
  useEffect(() => {
    const fetchApplications = async () => {
      if (!selectedEnvironment) return;
      
      try {
        setLoading(true);
        // Fetch applications filtered by environment
        const data = await apiService.get(`/applications/reports-filter?env_id=${selectedEnvironment}`);
        setApplications(data || []);
        
        // Auto-select the first application if available
        if (data && data.length > 0) {
          setSelectedApplication(data[0].app_id);
        } else {
          setSelectedApplication("");
        }
      } catch (error) {
        console.error("Error fetching applications:", error);
        setApplications([]);
        setSelectedApplication("");
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, [selectedEnvironment]); // Re-fetch when environment changes

  // Fetch Boomi environments for Failure Report tab
  useEffect(() => {
    if (selectedTab === "failure") {
      apiService
        .get("/reports/environments")
        .then((res) => {
          setBoomiEnvironments(res || []);
          // Auto-select first environment if available and not already selected
          if (res && res.length > 0 && !hasAutoSelectedEnv) {
            setBoomiEnvId(res[0].env_id);
            setHasAutoSelectedEnv(true);
          }
        })
        .catch((err) => console.error("Boomi env load error", err));
    } else {
      // Reset when switching away from failure tab
      setHasAutoSelectedEnv(false);
      setHasAutoSelectedApp(false);
    }
  }, [selectedTab, hasAutoSelectedEnv]);

  // Fetch Boomi applications when environment changes
  useEffect(() => {
    if (selectedTab === "failure" && boomiEnvId) {
      apiService
        .get(`/reports/applications?env_id=${boomiEnvId}`)
        .then((res) => {
          setBoomiApplications(res || []);
          // Auto-select first application if available and not already selected
          if (res && res.length > 0 && !hasAutoSelectedApp) {
            setBoomiAppId(res[0].app_id);
            setHasAutoSelectedApp(true);
          }
        })
        .catch((err) => console.error("Boomi app load error", err));
    } else if (selectedTab === "failure") {
      setBoomiApplications([]);
      setBoomiAppId("");
      setHasAutoSelectedApp(false);
    }
  }, [selectedTab, boomiEnvId, hasAutoSelectedApp]);

  const renderActiveReport = () => {
    // Check for application selection based on active tab
    if (selectedTab === "monitored" && !selectedApplication) {
      return (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            minHeight: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Please select an application to view reports
          </Typography>
        </Box>
      );
    }

    if (selectedTab === "failure" && !boomiAppId) {
      return (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            minHeight: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Please select an application to view failure reports
          </Typography>
        </Box>
      );
    }

    // Get application name from applications array
    const getApplicationName = () => {
      const app = applications.find(a => a.app_id === selectedApplication);
      return app ? app.name : selectedApplication;
    };

    // Render based on selected tab and period
    if (selectedTab === "monitored") {
      // GARPAC Reports
      switch (selectedPeriod) {
        case "daily":
          return (
            <GarpacDailyReports 
              ref={dailyReportRef}
              selectedApplication={selectedApplication}
              applicationName={getApplicationName()}
              selectedEnvironment={selectedEnvironment}
              dateMonitored={dateMonitored}
              setDateMonitored={setDateMonitored}
              showEmailSubject={showEmailSubject}
              setShowEmailSubject={setShowEmailSubject}
              emailConfigOpen={emailConfigOpen}
              setEmailConfigOpen={setEmailConfigOpen}
            />
          );
        case "weekly":
          return (
            <GarpacWeeklyReports 
              ref={weeklyReportRef}
              selectedApplication={selectedApplication}
              applicationName={getApplicationName()}
              selectedEnvironment={selectedEnvironment}
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
              showEmailSubject={showEmailSubject}
              setShowEmailSubject={setShowEmailSubject}
              emailConfigOpen={emailConfigOpen}
              setEmailConfigOpen={setEmailConfigOpen}
            />
          );
        case "monthly":
          return (
            <GarpacMonthlyReports 
              ref={monthlyReportRef}
              selectedApplication={selectedApplication}
              applicationName={getApplicationName()}
              selectedEnvironment={selectedEnvironment}
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
              showEmailSubject={showEmailSubject}
              setShowEmailSubject={setShowEmailSubject}
              emailConfigOpen={emailConfigOpen}
              setEmailConfigOpen={setEmailConfigOpen}
            />
          );
        default:
          return (
            <GarpacDailyReports 
              ref={dailyReportRef}
              selectedApplication={selectedApplication}
              applicationName={getApplicationName()}
              selectedEnvironment={selectedEnvironment}
              dateMonitored={dateMonitored}
              setDateMonitored={setDateMonitored}
              showEmailSubject={showEmailSubject}
              setShowEmailSubject={setShowEmailSubject}
              emailConfigOpen={emailConfigOpen}
              setEmailConfigOpen={setEmailConfigOpen}
            />
          );
      }
    } else {
      // Boomi Reports (Failure Report tab) - Render based on period
      switch (selectedPeriod) {
        case "daily":
          return (
            <BoomiReports 
              ref={boomiDailyReportRef}
              period="daily"
              dateType={boomiDateType}
              fromDate={boomiFromDate}
              toDate={boomiToDate}
              envId={boomiEnvId}
              appId={boomiAppId}
              showEmailSubject={boomiShowEmailSubject}
              setShowEmailSubject={setBoomiShowEmailSubject}
              emailConfigOpen={boomiEmailConfigOpen}
              setEmailConfigOpen={setBoomiEmailConfigOpen}
            />
          );
        case "weekly":
          return (
            <BoomiReports 
              ref={boomiWeeklyReportRef}
              period="weekly"
              dateType={boomiDateType}
              fromDate={boomiFromDate}
              toDate={boomiToDate}
              envId={boomiEnvId}
              appId={boomiAppId}
              showEmailSubject={boomiShowEmailSubject}
              setShowEmailSubject={setBoomiShowEmailSubject}
              emailConfigOpen={boomiEmailConfigOpen}
              setEmailConfigOpen={setBoomiEmailConfigOpen}
            />
          );
        case "monthly":
          return (
            <BoomiReports 
              ref={boomiMonthlyReportRef}
              period="monthly"
              dateType={boomiDateType}
              fromDate={boomiFromDate}
              toDate={boomiToDate}
              envId={boomiEnvId}
              appId={boomiAppId}
              showEmailSubject={boomiShowEmailSubject}
              setShowEmailSubject={setBoomiShowEmailSubject}
              emailConfigOpen={boomiEmailConfigOpen}
              setEmailConfigOpen={setBoomiEmailConfigOpen}
            />
          );
        default:
          return (
            <BoomiReports 
              ref={boomiDailyReportRef}
              period="daily"
              dateType={boomiDateType}
              fromDate={boomiFromDate}
              toDate={boomiToDate}
              envId={boomiEnvId}
              appId={boomiAppId}
              showEmailSubject={boomiShowEmailSubject}
              setShowEmailSubject={setBoomiShowEmailSubject}
              emailConfigOpen={boomiEmailConfigOpen}
              setEmailConfigOpen={setBoomiEmailConfigOpen}
            />
          );
      }
    }
  };

  // Handle download based on current period and tab
  const handleDownload = () => {
    if (selectedTab === "monitored") {
      // GARPAC reports
      if (selectedPeriod === "daily" && dailyReportRef.current) {
        dailyReportRef.current.downloadDashboard();
      } else if (selectedPeriod === "weekly" && weeklyReportRef.current) {
        weeklyReportRef.current.downloadDashboard();
      } else if (selectedPeriod === "monthly" && monthlyReportRef.current) {
        monthlyReportRef.current.downloadDashboard();
      }
    } else {
      // Boomi reports - use appropriate ref based on period
      if (selectedPeriod === "daily" && boomiDailyReportRef.current) {
        boomiDailyReportRef.current.downloadDashboard();
      } else if (selectedPeriod === "weekly" && boomiWeeklyReportRef.current) {
        boomiWeeklyReportRef.current.downloadDashboard();
      } else if (selectedPeriod === "monthly" && boomiMonthlyReportRef.current) {
        boomiMonthlyReportRef.current.downloadDashboard();
      }
    }
  };

  // Handle email send based on current period and tab
  const handleSendEmail = () => {
    if (selectedTab === "monitored") {
      setEmailConfirmOpen(true);
    } else {
      setBoomiEmailConfirmOpen(true);
    }
  };

  const confirmSendEmail = () => {
    setEmailConfirmOpen(false);
    if (selectedPeriod === "daily" && dailyReportRef.current) {
      dailyReportRef.current.sendEmail();
    } else if (selectedPeriod === "weekly" && weeklyReportRef.current) {
      weeklyReportRef.current.sendEmail();
    } else if (selectedPeriod === "monthly" && monthlyReportRef.current) {
      monthlyReportRef.current.sendEmail();
    }
  };

  const confirmBoomiSendEmail = () => {
    setBoomiEmailConfirmOpen(false);
    if (selectedPeriod === "daily" && boomiDailyReportRef.current) {
      boomiDailyReportRef.current.sendEmail();
    } else if (selectedPeriod === "weekly" && boomiWeeklyReportRef.current) {
      boomiWeeklyReportRef.current.sendEmail();
    } else if (selectedPeriod === "monthly" && boomiMonthlyReportRef.current) {
      boomiMonthlyReportRef.current.sendEmail();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#EBF3FF",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* TOP FILTER BAR WITH ALL CONTROLS */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          p: 1,
          borderBottom: `2px solid ${BLUE_BORDER}`,
          bgcolor: BG_PANEL,
        }}
      >
        {/* Applications and Period - Only show for Monitored Report tab */}
        {selectedTab === "monitored" && (
          <>
            {/* Environment Selector - First */}
            <FormControl size="small" sx={{ 
              minWidth: 140, 
              bgcolor: "#fff",
              "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              "& .MuiSelect-select": { fontSize: "0.875rem" },
              "& .MuiMenuItem-root": { 
                fontSize: "0.875rem",
                minHeight: "36px",
                padding: "6px 16px"
              }
            }}>
              <InputLabel id="environment-label">Environment</InputLabel>
              <Select
                labelId="environment-label"
                value={selectedEnvironment}
                onChange={(e) => setSelectedEnvironment(e.target.value)}
                label="Environment"
                disabled={loading}
                displayEmpty={false}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "0.875rem",
                        minHeight: "36px",
                        padding: "6px 16px"
                      }
                    }
                  }
                }}
                sx={{
                  bgcolor: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": {
                    
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  
                    borderWidth: 2,
                  },
                }}
              >
                {environments.map((env) => (
                  <MenuItem key={env.env_id} value={env.env_id}>
                    {env.name || env.env_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Applications Selector - Second */}
            <FormControl size="small" sx={{ 
              minWidth: 140, 
              bgcolor: "#fff",
              "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              "& .MuiSelect-select": { fontSize: "0.875rem" },
              "& .MuiMenuItem-root": { 
                fontSize: "0.875rem",
                minHeight: "36px",
                padding: "6px 16px"
              }
            }}>
              <InputLabel id="applications-label">Applications</InputLabel>
              <Select
                labelId="applications-label"
                value={selectedApplication}
                onChange={(e) => setSelectedApplication(e.target.value)}
                label="Applications"
                disabled={loading}
                displayEmpty={false}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "0.875rem",
                        minHeight: "36px",
                        padding: "6px 16px"
                      }
                    }
                  }
                }}
                sx={{
                  bgcolor: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": {
                    
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  
                    borderWidth: 2,
                  },
                }}
              >
                {applications.map((app) => (
                  <MenuItem key={app.app_id} value={app.app_id}>
                    {app.name || app.app_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Period Selector */}
            <FormControl size="small" sx={{ 
              minWidth: 140, 
              bgcolor: "#fff",
              "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              "& .MuiSelect-select": { fontSize: "0.875rem" },
              "& .MuiMenuItem-root": { 
                fontSize: "0.875rem",
                minHeight: "36px",
                padding: "6px 16px"
              }
            }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                label="Period"
                disabled={!selectedApplication || loading}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "0.875rem",
                        minHeight: "36px",
                        padding: "6px 16px"
                      }
                    }
                  }
                }}
              >
                <MenuItem value="daily">Daily Reports</MenuItem>
                <MenuItem value="weekly">Weekly Reports</MenuItem>
                <MenuItem value="monthly">Monthly Reports</MenuItem>
              </Select>
            </FormControl>
          </>
        )}

        {/* Period-specific date controls - Only show for Monitored Report tab */}
        {selectedApplication && selectedTab === "monitored" && (
          <>
            {/* Daily Report Controls */}
            {selectedPeriod === "daily" && (
              <DatePicker
                selected={toDateObj(dateMonitored)}
                onChange={(date) => {
                  const iso = date ? date.toLocaleDateString("en-CA") : "";
                  setDateMonitored(iso);
                }}
                dateFormat="dd-MMM-yyyy"
                placeholderText="Date Monitored"
                isClearable
                withPortal
                customInput={
                  <TextField
                    label="Date Monitored"
                    variant="outlined"
                    size="small"
                    sx={{
                      width: 140,
                      bgcolor: "white",
                      "& .MuiInputBase-root": { height: 40 },
                      "& .MuiInputBase-input": { fontSize: "0.875rem" },
                      "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                }
              />
            )}

            {/* Weekly Report Controls */}
            {selectedPeriod === "weekly" && (
              <>
                <DatePicker
                  selected={toDateObj(dateFrom)}
                  onChange={(date) => {
                    const iso = date ? date.toLocaleDateString("en-CA") : "";
                    setDateFrom(iso);
                  }}
                  dateFormat="dd-MMM-yyyy"
                  placeholderText="From"
                  isClearable
                  withPortal
                  customInput={
                    <TextField
                      label="From"
                      variant="outlined"
                      size="small"
                      sx={{
                        width: 140,
                        bgcolor: "white",
                        "& .MuiInputBase-root": { height: 40 },
                        "& .MuiInputBase-input": { fontSize: "0.875rem" },
                        "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                      }}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  }
                />
                <DatePicker
                  selected={toDateObj(dateTo)}
                  onChange={(date) => {
                    const iso = date ? date.toLocaleDateString("en-CA") : "";
                    setDateTo(iso);
                  }}
                  dateFormat="dd-MMM-yyyy"
                  placeholderText="To"
                  isClearable
                  withPortal
                  minDate={toDateObj(dateFrom)}
                  customInput={
                    <TextField
                      label="To"
                      variant="outlined"
                      size="small"
                      sx={{
                        width: 140,
                        bgcolor: "white",
                        "& .MuiInputBase-root": { height: 40 },
                        "& .MuiInputBase-input": { fontSize: "0.875rem" },
                        "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                      }}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  }
                />
              </>
            )}

            {/* Monthly Report Controls */}
            {selectedPeriod === "monthly" && (
              <TextField
                type="month"
                size="small"
                label="Select Month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                sx={{ 
                  width: 160, 
                  bgcolor: "white",
                  "& .MuiInputBase-root": { height: 40 },
                  "& .MuiInputBase-input": { fontSize: "0.875rem" },
                  "& .MuiInputLabel-root": { fontSize: "0.875rem" }
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            )}

            {/* Common controls for all periods */}
            <Tooltip title="Download PNG">
              <IconButton
                onClick={handleDownload}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <FileDownloadIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Send Email">
              <IconButton
                onClick={handleSendEmail}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <EmailIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Email Settings">
              <IconButton
                onClick={() => setEmailConfigOpen(true)}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIconSVG style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Boomi/Failure Report filters */}
        {selectedTab === "failure" && (
          <>
            {/* Environment */}
            <FormControl size="small" sx={{ 
              minWidth: 140, 
              bgcolor: "#fff",
              "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              "& .MuiSelect-select": { fontSize: "0.875rem" },
              "& .MuiMenuItem-root": { 
                fontSize: "0.875rem",
                minHeight: "36px",
                padding: "6px 16px"
              }
            }}>
              <InputLabel>Environment</InputLabel>
              <Select
                value={boomiEnvId}
                onChange={(e) => {
                  setBoomiEnvId(e.target.value);
                  setBoomiAppId(""); // reset app when env changes
                }}
                label="Environment"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "0.875rem",
                        minHeight: "36px",
                        padding: "6px 16px"
                      }
                    }
                  }
                }}
              >
                <MenuItem value="">-- Select Environment --</MenuItem>
                {boomiEnvironments.map((env) => (
                  <MenuItem key={env.env_id} value={env.env_id}>
                    {env.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Application */}
            <FormControl size="small" sx={{ 
              minWidth: 140, 
              bgcolor: "#fff",
              "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              "& .MuiSelect-select": { fontSize: "0.875rem" },
              "& .MuiMenuItem-root": { 
                fontSize: "0.875rem",
                minHeight: "36px",
                padding: "6px 16px"
              }
            }}>
              <InputLabel>Application</InputLabel>
              <Select
                value={boomiAppId}
                onChange={(e) => setBoomiAppId(e.target.value)}
                label="Application"
                disabled={!boomiEnvId}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "0.875rem",
                        minHeight: "36px",
                        padding: "6px 16px"
                      }
                    }
                  }
                }}
              >
                <MenuItem value="">-- Select Application --</MenuItem>
                {boomiApplications.map((app) => (
                  <MenuItem key={app.app_id} value={app.app_id}>
                    {app.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Period Selector for Boomi */}
            <FormControl size="small" sx={{ 
              minWidth: 140, 
              bgcolor: "#fff",
              "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              "& .MuiSelect-select": { fontSize: "0.875rem" },
              "& .MuiMenuItem-root": { 
                fontSize: "0.875rem",
                minHeight: "36px",
                padding: "6px 16px"
              }
            }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={selectedPeriod}
                onChange={(e) => {
                  const p = e.target.value;
                  setSelectedPeriod(p);

                  // Auto-adjust dates based on period (matching original BoomiDashboard logic)
                  if (p === "daily") {
                    const today = new Date().toISOString().split("T")[0];
                    setBoomiFromDate(today);
                    setBoomiToDate(today);
                  } else if (p === "weekly") {
                    // Get current week (Sunday to Saturday)
                    const curr = new Date();
                    const dayOfWeek = curr.getDay(); // 0 = Sunday, 6 = Saturday
                    
                    const firstDay = new Date(curr);
                    firstDay.setDate(curr.getDate() - dayOfWeek); // Sunday
                    
                    const lastDay = new Date(curr);
                    lastDay.setDate(curr.getDate() + (6 - dayOfWeek)); // Saturday
                    
                    const formatDate = (d) => d.toISOString().split("T")[0];
                    setBoomiFromDate(formatDate(firstDay));
                    setBoomiToDate(formatDate(lastDay));
                  } else if (p === "monthly") {
                    // Get current month
                    const now = new Date();
                    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                    const formatLocalDate = (date) => {
                      const yyyy = date.getFullYear();
                      const mm = String(date.getMonth() + 1).padStart(2, "0");
                      const dd = String(date.getDate()).padStart(2, "0");
                      return `${yyyy}-${mm}-${dd}`;
                    };
                    setBoomiFromDate(formatLocalDate(firstDay));
                    setBoomiToDate(formatLocalDate(lastDay));
                  }
                }}
                label="Period"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "0.875rem",
                        minHeight: "36px",
                        padding: "6px 16px"
                      }
                    }
                  }
                }}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>

            {/* Date Type */}
            <FormControl size="small" sx={{ 
              minWidth: 140, 
              bgcolor: "#fff",
              "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              "& .MuiSelect-select": { fontSize: "0.875rem" },
              "& .MuiMenuItem-root": { 
                fontSize: "0.875rem",
                minHeight: "36px",
                padding: "6px 16px"
              }
            }}>
              <InputLabel>Date Type</InputLabel>
              <Select
                value={boomiDateType}
                onChange={(e) => setBoomiDateType(e.target.value)}
                label="Date Type"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "0.875rem",
                        minHeight: "36px",
                        padding: "6px 16px"
                      }
                    }
                  }
                }}
              >
                <MenuItem value="reported">Reported Date</MenuItem>
                <MenuItem value="received">Received Date</MenuItem>
              </Select>
            </FormControl>

            {/* From Date */}
            <DatePicker
              selected={toDateObj(boomiFromDate)}
              onChange={(date) => {
                const iso = date ? date.toLocaleDateString("en-CA") : "";
                setBoomiFromDate(iso);
              }}
              dateFormat="dd-MMM-yyyy"
              placeholderText="From"
              isClearable
              withPortal
              customInput={
                <TextField
                  label="From"
                  variant="outlined"
                  size="small"
                  sx={{
                    width: 140,
                    bgcolor: "white",
                    "& .MuiInputBase-root": { height: 40 },
                    "& .MuiInputBase-input": { fontSize: "0.875rem" },
                    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              }
            />

            {/* To Date */}
            <DatePicker
              selected={toDateObj(boomiToDate)}
              onChange={(date) => {
                const iso = date ? date.toLocaleDateString("en-CA") : "";
                setBoomiToDate(iso);
              }}
              dateFormat="dd-MMM-yyyy"
              placeholderText="To"
              isClearable
              withPortal
              minDate={toDateObj(boomiFromDate)}
              customInput={
                <TextField
                  label="To"
                  variant="outlined"
                  size="small"
                  sx={{
                    width: 140,
                    bgcolor: "white",
                    "& .MuiInputBase-root": { height: 40 },
                    "& .MuiInputBase-input": { fontSize: "0.875rem" },
                    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              }
            />

            {/* Download Button */}
            <Tooltip title="Download PNG">
              <IconButton
                onClick={handleDownload}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <FileDownloadIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Send Email">
              <IconButton
                onClick={handleSendEmail}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <EmailIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Email Settings">
              <IconButton
                onClick={() => setBoomiEmailConfigOpen(true)}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIconSVG style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>
          </>
        )}

        {loading && <CircularProgress size={24} sx={{ ml: 1 }} />}
      </Box>

      {/* MAIN CONTENT AREA */}
      <Box sx={{ flex: 1, p: 2, overflowY: "auto" }}>
        {renderActiveReport()}
      </Box>

      {/* EMAIL CONFIRMATION DIALOG */}
      <Dialog
        open={emailConfirmOpen}
        onClose={() => setEmailConfirmOpen(false)}
        aria-labelledby="email-confirm-dialog-title"
      >
        <DialogTitle id="email-confirm-dialog-title">
          Send Report via Email
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to send this report via email?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEmailConfirmOpen(false)} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmSendEmail} 
            variant="contained"
            sx={{ bgcolor: BLUE_BORDER }}
            autoFocus
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* BOOMI EMAIL CONFIRMATION DIALOG */}
      <Dialog
        open={boomiEmailConfirmOpen}
        onClose={() => setBoomiEmailConfirmOpen(false)}
        aria-labelledby="boomi-email-confirm-dialog-title"
      >
        <DialogTitle id="boomi-email-confirm-dialog-title">
          Send Failure Report via Email
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to send this failure report via email?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBoomiEmailConfirmOpen(false)} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmBoomiSendEmail} 
            variant="contained"
            sx={{ bgcolor: BLUE_BORDER }}
            autoFocus
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GarpacReportsContainer;
